/**
 * The unsampled event log, read out of S3 on demand.
 *
 * WHY THIS IS A SEPARATE PANEL AND NOT MORE ROWS IN THE DETAIL PAYLOAD. Two stores
 * hold two different things and this component is the second one made visible:
 *
 *   · `session_events` in Postgres is an INDEX — roughly eighteen rows per call,
 *     the lifecycle transitions and errors you filter ACROSS sessions. It ships
 *     inline with the session detail because the transcript view needs a handful of
 *     them to place its gap markers.
 *   · The JSONL in S3 is the LOG — 548 events and 228 KB for one 84-second call,
 *     every provider frame, unsampled. That is what answers "explain THIS call",
 *     and putting it in Postgres would be ~200M rows a year to answer a question
 *     that is always about one call.
 *
 * So this fetches on demand, and only when a human asks. It is collapsed by
 * default for the same reason: nobody opens a session page wanting a quarter of a
 * megabyte of provider frames, right up until the moment they do.
 *
 * TRUNCATION IS SHOWN, NEVER IMPLIED. The endpoint reports `matchedTotal` for the
 * whole object alongside the page it returned, and this renders both. A viewer that
 * displayed the first thousand of twelve thousand events without saying so would be
 * the same species of lie as a completeness verdict computed from missing bytes —
 * and this is the screen where that would be least forgivable.
 *
 * `fetchPage` IS INJECTED rather than imported. It keeps this component free of any
 * coupling to the API module, which means it can be exercised against a fixture
 * with no network at all, and the redaction contract stays a property of the
 * endpoint rather than something this file could accidentally work around.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronRight, DownloadIcon, RefreshCwIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
// The shape is the API's, so it is declared with the other response shapes. Only
// the TYPE is imported — the fetcher stays injected, which is what keeps this
// component testable against a fixture with no network.
import type { EventLogPage } from '@/api'


export interface EventLogProps {
  /** `(after, kind) => page`. Injected; see the note at the top of the file. */
  fetchPage: (after: number, kind: string, signal: AbortSignal) => Promise<EventLogPage>
  /** Start expanded — for hosts that are themselves opened on demand (the
   *  session page's event-log drawer), where a second click would be a moat. */
  defaultOpen?: boolean
}

/** The provider prefixes every sideband frame with `oai.`; ours have no prefix. */
function eventName(ev: unknown): string {
  if (ev === null || typeof ev !== 'object') return '?'
  const o = ev as { kind?: unknown; type?: unknown }
  if (typeof o.kind === 'string') return o.kind
  if (typeof o.type === 'string') return o.type
  return '?'
}

function eventSeq(ev: unknown): number | null {
  if (ev === null || typeof ev !== 'object') return null
  const s = (ev as { seq?: unknown }).seq
  return typeof s === 'number' ? s : null
}

function eventAtMs(ev: unknown): number | null {
  if (ev === null || typeof ev !== 'object') return null
  const t = (ev as { atMs?: unknown; at_ms?: unknown })
  const v = typeof t.atMs === 'number' ? t.atMs : typeof t.at_ms === 'number' ? t.at_ms : null
  return v
}

function severityOf(ev: unknown): string {
  if (ev === null || typeof ev !== 'object') return 'info'
  const s = (ev as { severity?: unknown }).severity
  return typeof s === 'string' ? s : 'info'
}

export function EventLogPanel({ fetchPage, defaultOpen = false }: EventLogProps) {
  const [open, setOpen] = useState(defaultOpen)
  const [page, setPage] = useState<EventLogPage | null>(null)
  const [rows, setRows] = useState<unknown[]>([])
  const [kind, setKind] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const abort = useRef<AbortController | null>(null)

  const load = useCallback(
    async (after: number, k: string, append: boolean) => {
      abort.current?.abort()
      const ac = new AbortController()
      abort.current = ac
      setPending(true)
      setError(null)
      try {
        const p = await fetchPage(after, k, ac.signal)
        setPage(p)
        setRows((prev) => (append ? [...prev, ...p.rows] : p.rows))
      } catch (e) {
        if (ac.signal.aborted) return
        // The 409 is a real answer, not a failure: a call whose log has not been
        // checkpointed yet has nothing to show, and saying so beats an empty list
        // that reads as "this call produced no events".
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        if (!ac.signal.aborted) setPending(false)
      }
    },
    [fetchPage],
  )

  // Fetch only when opened, and only once. This is a quarter of a megabyte.
  useEffect(() => {
    if (open && page === null && !pending) void load(0, kind, false)
  }, [open, page, pending, kind, load])

  useEffect(() => () => abort.current?.abort(), [])

  const toggleRow = (i: number) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return next
    })

  return (
    <section className="rounded-lg border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
      >
        <ChevronRight className={cn('size-4 shrink-0 transition-transform', open && 'rotate-90')} />
        <span className="text-sm font-semibold uppercase tracking-wide">The full event log</span>
        <span className="text-muted-foreground text-xs">
          every provider frame, unsampled · read from S3 on demand
        </span>
      </button>

      {open && (
        <div className="space-y-3 border-t px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              placeholder="filter by event name, e.g. output_audio_buffer"
              className="h-8 max-w-xs text-xs"
              onKeyDown={(e) => {
                if (e.key === 'Enter') void load(0, kind, false)
              }}
            />
            <Button size="sm" variant="outline" onClick={() => void load(0, kind, false)} disabled={pending}>
              <RefreshCwIcon className={cn('size-3.5', pending && 'animate-spin')} />
              Apply
            </Button>
            {page !== null && (
              // A real download, not a data: link — browsers refuse top-level
              // navigation to data: URLs, so the old "open in a new tab" link
              // silently did nothing. Blob + <a download> saves a file named
              // after the session. It saves the rows LOADED so far, which the
              // count line above states precisely.
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground ml-auto inline-flex cursor-pointer items-center gap-1 text-xs underline"
                onClick={() => {
                  const id = page.key.match(/\/s=([^/]+)\//)?.[1]
                  const url = URL.createObjectURL(
                    new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' }),
                  )
                  const a = document.createElement('a')
                  a.href = url
                  a.download = id ? `events-${id}.json` : 'events.json'
                  document.body.appendChild(a)
                  a.click()
                  a.remove()
                  URL.revokeObjectURL(url)
                }}
              >
                <DownloadIcon className="size-3.5" />
                download as JSON
              </button>
            )}
          </div>

          {error !== null && (
            <p className="text-xs text-[var(--destructive)]">{error}</p>
          )}

          {page !== null && (
            <p className="text-muted-foreground text-xs">
              Showing <strong className="text-foreground">{rows.length}</strong> of{' '}
              <strong className="text-foreground">{page.matchedTotal}</strong>
              {page.filteredByKind !== null ? ` events matching “${page.filteredByKind}”` : ' events'}
              {page.truncated && ' — MORE REMAIN, this is not the whole log'}.{' '}
              {page.malformedLines > 0 && (
                <>
                  <strong className="text-foreground">{page.malformedLines}</strong> line
                  {page.malformedLines === 1 ? '' : 's'} in the object could not be parsed
                  {page.malformedLines === 1
                    ? ' — one is normal, it is the checkpoint boundary where the object ends mid-line'
                    : ' — more than one means the writer has a problem'}
                  .{' '}
                </>
              )}
              <span className="font-mono">{page.key}</span>
            </p>
          )}

          {rows.length > 0 && (
            <ol className="divide-y text-xs">
              {rows.map((ev, i) => {
                const sev = severityOf(ev)
                const seq = eventSeq(ev)
                const at = eventAtMs(ev)
                const isOpen = expanded.has(i)
                return (
                  <li key={`${seq ?? 'x'}-${i}`} className="py-1">
                    <button
                      type="button"
                      onClick={() => toggleRow(i)}
                      className="flex w-full items-baseline gap-2 text-left font-mono"
                    >
                      <span className="text-muted-foreground w-10 shrink-0 tabular-nums">
                        {seq ?? '—'}
                      </span>
                      <span className="text-muted-foreground w-20 shrink-0 tabular-nums">
                        {at === null ? '—' : new Date(at).toISOString().slice(11, 23)}
                      </span>
                      <span
                        className={cn(
                          'grow break-all',
                          sev === 'error' && 'text-[var(--destructive)] font-semibold',
                          sev === 'warn' && 'font-semibold',
                        )}
                      >
                        {eventName(ev)}
                      </span>
                      <ChevronRight
                        className={cn('size-3 shrink-0 transition-transform', isOpen && 'rotate-90')}
                      />
                    </button>
                    {isOpen && (
                      <pre className="bg-muted mt-1 max-h-64 overflow-auto rounded p-2 text-[11px] leading-tight">
                        {JSON.stringify(ev, null, 2)}
                      </pre>
                    )}
                  </li>
                )
              })}
            </ol>
          )}

          {page !== null && page.truncated && page.nextAfter !== null && (
            <Button
              size="sm"
              variant="outline"
              disabled={pending}
              onClick={() => void load(page.nextAfter ?? 0, kind, true)}
            >
              Load the next {Math.min(page.returned, page.matchedTotal - rows.length)}
            </Button>
          )}

          <p className="text-muted-foreground text-[11px]">
            Verbatim, with one exception: any <code>headers</code> key at any depth reads{' '}
            <em>redacted</em> — custom tool and webhook headers are customer credentials.
            Everything else, the system prompt and the tool JSON Schemas included, is exactly
            what crossed the sideband: <code>session.created</code> and{' '}
            <code>session.updated</code> are verbatim echoes of the effective session object,
            which is what makes this the troubleshooting view. The log is proxied rather than
            handed over as a presigned URL so that credential scrub always runs.
          </p>
        </div>
      )}
    </section>
  )
}
